import { createStore } from 'zustand';
import type { StateCreator } from 'zustand';
import { WebinarError } from 'react-scale/components/WebinarSessions/constants';
import {
  CohortMappingInput,
  CollectionSessionWithUserIds,
  RecurringType,
} from '@scale/shared/webinarSessions/types';
import { isEqual, isNil } from 'lodash';
import { SessionTime, RepeatType } from 'react-scale/components/WebinarSessions/types';
import {
  getNewStartDateTime,
  getStartTimeFromDate,
  getEndTimeFromStartTime,
  timeDifferenceInMinutes,
} from 'react-scale/components/WebinarSessions/hooks/calculateFutureOccurrences';
import { NavigationItems } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarFormLayout';

interface EditWebinarSessionStore {
  // General webinar setting fields
  name: string | null;
  setName: (name: string | null) => void;
  description: string | null;
  setDescription: (description: string | null) => void;
  rsvpMax: number | null;
  setRsvpMax: (n: number | null) => void;
  minAttendanceDuration: number | null;
  setMinAttendanceDuration: (n: number | null) => void;
  expectedNumberOfAttendees: number | null;
  setExpectedNumberOfAttendees: (n: number | null) => void;
  project: string | null;
  setProject: (id: string | null) => void;
  resetWebinarSettingFields: (collection: CollectionSessionWithUserIds) => void;
  getWebinarSettingsChanges: (collection: CollectionSessionWithUserIds) => {
    isNameChanged: boolean;
    isDescriptionChanged: boolean;
    isRsvpMaxChanged: boolean;
    isMinAttendanceDurationChanged: boolean;
    isExpectedNumberOfAttendeesChanged: boolean;
    isProjectChanged: boolean;
  };

  // Invitees fields
  invitedUsers: string[];
  setInvitedUsers: (ids: string[]) => void;
  audience: string | null;
  setAudience: (id: string | null) => void;
  audienceBucket: number | null;
  setAudienceBucket: (index: number | null) => void;
  invitedWorkerTeams: string[];
  setInvitedWorkerTeams: (ids: string[]) => void;
  invitedQualifications: string[];
  setInvitedQualifications: (ids: string[]) => void;
  invitedSquads: string[];
  setInvitedSquads: (ids: string[]) => void;
  excludeEmptyQueueUsers: boolean;
  setExcludeEmptyQueueUsers: (excludeEmptyQueueUsers: boolean) => void;
  cohortMappings: CohortMappingInput[];
  setCohortMappings: (mappings: CohortMappingInput[]) => void;
  resetWebinarInviteeFields: (collection: CollectionSessionWithUserIds) => void;
  getWebinarInviteeChanges: (collection: CollectionSessionWithUserIds) => {
    isInvitedUsersChanged: boolean;
    isAudienceChanged: boolean;
    isAudienceBucketChanged: boolean;
    isInvitedWorkerTeamsChanged: boolean;
    isInvitedQualificationsChanged: boolean;
    isInvitedSquadsChanged: boolean;
    isExcludeEmptyQueueUsersChanged: boolean;
    isCohortMappingsChanged: boolean;
  };
  allInvitedUserIds: string[];
  setAllInvitedUserIds: (ids: string[]) => void;

  // Session fields
  sessionType: RecurringType;
  setSessionType: (sessionType: RecurringType) => void;
  hostedBy: string[];
  setHostedBy: (hostedBy: string[]) => void;
  // Recurring session fields
  occurrences: SessionTime[];
  setOccurrences: (o: SessionTime[]) => void;
  numOccurrences: number | null;
  setNumOccurrences: (n: number | null) => void;
  repeatNumber: number | null;
  setRepeatNumber: (n: number | null) => void;
  repeatType: RepeatType;
  setRepeatType: (t: RepeatType) => void;
  daysOfTheWeek: string[];
  setDaysOfTheWeek: (d: string[]) => void;
  additionalTimesOfDay: SessionTime[];
  setAdditionalTimesOfDay: (t: SessionTime[]) => void;
  // Fields for the first new session
  eventStart: Date | null;
  setEventStart: (d: Date | null) => void;
  duration: number | null;
  setDuration: (d: number | null) => void;
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;
  startTime: string;
  setStartTime: (t: string) => void;
  endTime: string;
  setEndTime: (t: string) => void;
  // Multiple session fields
  additionalSessions: SessionTime[];
  setAdditionalSessions: (o: SessionTime[]) => void;

  // Editing specific fields
  createNewSessions: boolean;
  setCreateNewSessions: (createNewSessions: boolean) => void;
  sessionIdsToCancel: { sessionId: string; reason: string }[];
  setSessionIdsToCancel: (sessionIdsToCancel: { sessionId: string; reason: string }[]) => void;
  sessionIdsWithHostChanges: { sessionId: string; hostedBy: string[] }[];
  setSessionIdsWithHostChanges: (
    sessionIdsWithHostChanges: { sessionId: string; hostedBy: string[] }[],
  ) => void;
  resetWebinarSessionFields: () => void;
  getWebinarSessionChanges: () => {
    isEventStartChanged: boolean;
    isOccurrencesChanged: boolean;
    isAdditionalSessionsChanged: boolean;
    isSessionIdsToCancelChanged: boolean;
    isHostedByChanged: boolean;
  };
  resetWebinarSessionFieldsOnEdit: () => void;
  resetWebinarSessionFieldsOnCreate: () => void;

  getErrors: (navItem: NavigationItems) => WebinarError[];
  getWebinarSettingsErrors: () => WebinarError[];
  getWebinarInviteeErrors: () => WebinarError[];
  getWebinarSessionErrors: () => WebinarError[];
  getRecurringErrors: () => Record<number, WebinarError>;
  getAdditionalSessionsErrors: () => Record<number, WebinarError>;
}

const editWebinarSessionStoreImpl: StateCreator<EditWebinarSessionStore> = (set, get) => ({
  name: null,
  setName: name => set({ name }),
  description: null,
  setDescription: description => set({ description }),
  rsvpMax: null,
  setRsvpMax: rsvpMax => set({ rsvpMax }),
  minAttendanceDuration: null,
  setMinAttendanceDuration: minAttendanceDuration => set({ minAttendanceDuration }),
  expectedNumberOfAttendees: null,
  setExpectedNumberOfAttendees: expectedNumberOfAttendees => set({ expectedNumberOfAttendees }),
  project: null,
  setProject: project => set({ project }),
  resetWebinarSettingFields: (collection: CollectionSessionWithUserIds) => {
    set({
      name: collection.name ?? null,
      description: collection.description ?? null,
      project: collection.project ?? null,
      rsvpMax: collection.rsvpMax ?? null,
      minAttendanceDuration: collection.minAttendanceDuration ?? null,
      expectedNumberOfAttendees: collection.expectedNumberOfAttendees ?? null,
    });
  },
  getWebinarSettingsChanges: (collection: CollectionSessionWithUserIds) => {
    const {
      name,
      description,
      project,
      rsvpMax,
      minAttendanceDuration,
      expectedNumberOfAttendees,
    } = get();

    const newProjectId = project && project.length > 0 ? project : null;
    return {
      isNameChanged: name !== (collection.name ?? null),
      isDescriptionChanged: description !== (collection.description ?? null),
      isRsvpMaxChanged: rsvpMax !== (collection.rsvpMax ?? null),
      isMinAttendanceDurationChanged:
        minAttendanceDuration !== (collection.minAttendanceDuration ?? null),
      isExpectedNumberOfAttendeesChanged:
        expectedNumberOfAttendees !== (collection.expectedNumberOfAttendees ?? null),
      isProjectChanged: newProjectId !== (collection.project ?? null),
    };
  },
  invitedUsers: [],
  setInvitedUsers: invitedUsers => set({ invitedUsers }),
  audience: null,
  setAudience: audience => set({ audience }),
  audienceBucket: null,
  setAudienceBucket: audienceBucket => set({ audienceBucket }),
  invitedWorkerTeams: [],
  setInvitedWorkerTeams: invitedWorkerTeams => set({ invitedWorkerTeams }),
  invitedQualifications: [],
  setInvitedQualifications: invitedQualifications => set({ invitedQualifications }),
  invitedSquads: [],
  setInvitedSquads: invitedSquads => set({ invitedSquads }),
  allInvitedUserIds: [],
  excludeEmptyQueueUsers: false,
  setExcludeEmptyQueueUsers: excludeEmptyQueueUsers => set({ excludeEmptyQueueUsers }),
  cohortMappings: [],
  setCohortMappings: cohortMappings => set({ cohortMappings }),
  setAllInvitedUserIds: allInvitedUserIds => set({ allInvitedUserIds }),
  resetWebinarInviteeFields: (collection: CollectionSessionWithUserIds) => {
    set({
      invitedUsers: collection.invitedUsers,
      audience: collection.audience ?? null,
      audienceBucket: collection.audienceBucket ?? null,
      invitedWorkerTeams: collection.invitedWorkerTeams,
      invitedQualifications: collection.invitedQualifications,
      invitedSquads: collection.invitedSquads,
      excludeEmptyQueueUsers: collection.excludeEmptyQueueUsers ?? false,
      cohortMappings: collection.cohortMappings ?? [],
    });
  },
  getWebinarInviteeChanges: (collection: CollectionSessionWithUserIds) => {
    const {
      invitedUsers,
      audience,
      audienceBucket,
      invitedWorkerTeams,
      invitedQualifications,
      invitedSquads,
      excludeEmptyQueueUsers,
      cohortMappings,
    } = get();

    const newAudience = audience && audience.length > 0 ? audience : null;
    return {
      isInvitedUsersChanged: !isEqual(invitedUsers, collection.invitedUsers),
      isAudienceChanged: newAudience !== (collection.audience ?? null),
      isAudienceBucketChanged: audienceBucket !== (collection.audienceBucket ?? null),
      isInvitedWorkerTeamsChanged: !isEqual(invitedWorkerTeams, collection.invitedWorkerTeams),
      isInvitedQualificationsChanged: !isEqual(
        invitedQualifications,
        collection.invitedQualifications,
      ),
      isInvitedSquadsChanged: !isEqual(invitedSquads, collection.invitedSquads),
      isExcludeEmptyQueueUsersChanged:
        excludeEmptyQueueUsers !== (collection.excludeEmptyQueueUsers ?? false),
      isCohortMappingsChanged: !isEqual(cohortMappings, collection.cohortMappings ?? []),
    };
  },
  sessionType: RecurringType.MULTI,
  setSessionType: sessionType => set({ sessionType }),
  hostedBy: [],
  setHostedBy: hostedBy => set({ hostedBy }),
  occurrences: [],
  setOccurrences: occurrences => set({ occurrences }),
  numOccurrences: null,
  setNumOccurrences: numOccurrences => set({ numOccurrences }),
  repeatNumber: null,
  setRepeatNumber: repeatNumber => set({ repeatNumber }),
  repeatType: RepeatType.DAYS,
  setRepeatType: repeatType => set({ repeatType }),
  daysOfTheWeek: [],
  setDaysOfTheWeek: daysOfTheWeek => set({ daysOfTheWeek }),
  additionalTimesOfDay: [],
  setAdditionalTimesOfDay: additionalTimesOfDay => set({ additionalTimesOfDay }),
  eventStart: null,
  setEventStart: eventStart => set({ eventStart }),
  duration: null,
  setDuration: duration => set({ duration }),
  selectedDate: new Date(),
  setSelectedDate: selectedDate => set({ selectedDate }),
  startTime: getStartTimeFromDate(new Date()),
  setStartTime: startTime => set({ startTime }),
  endTime: getEndTimeFromStartTime(getStartTimeFromDate(new Date())),
  setEndTime: endTime => set({ endTime }),
  additionalSessions: [],
  setAdditionalSessions: additionalSessions => set({ additionalSessions }),
  createNewSessions: false,
  setCreateNewSessions: createNewSessions => set({ createNewSessions }),
  sessionIdsToCancel: [],
  setSessionIdsToCancel: sessionIdsToCancel => set({ sessionIdsToCancel }),
  sessionIdsWithHostChanges: [],
  setSessionIdsWithHostChanges: sessionIdsWithHostChanges => set({ sessionIdsWithHostChanges }),
  resetWebinarSessionFields: () => {
    const now = new Date();
    const startTime = getStartTimeFromDate(now);
    const endTime = getEndTimeFromStartTime(startTime);
    set({
      sessionType: RecurringType.MULTI,
      hostedBy: [],
      occurrences: [],
      numOccurrences: null,
      repeatNumber: null,
      repeatType: RepeatType.DAYS,
      daysOfTheWeek: [],
      additionalTimesOfDay: [],
      eventStart: null,
      selectedDate: now,
      startTime,
      endTime,
      additionalSessions: [],
      sessionIdsToCancel: [],
      sessionIdsWithHostChanges: [],
      createNewSessions: false,
    });
  },
  resetWebinarSessionFieldsOnEdit: () => {
    set({
      sessionIdsToCancel: [],
      sessionIdsWithHostChanges: [],
    });
  },
  resetWebinarSessionFieldsOnCreate: () => {
    const now = new Date();
    const startTime = getStartTimeFromDate(now);
    const endTime = getEndTimeFromStartTime(startTime);
    set({
      sessionType: RecurringType.MULTI,
      occurrences: [],
      numOccurrences: null,
      repeatNumber: null,
      repeatType: RepeatType.DAYS,
      daysOfTheWeek: [],
      additionalTimesOfDay: [],
      eventStart: null,
      selectedDate: now,
      startTime,
      endTime,
      additionalSessions: [],
      createNewSessions: false,
    });
  },
  getWebinarSessionChanges: () => {
    const {
      eventStart,
      occurrences,
      additionalSessions,
      sessionType,
      sessionIdsToCancel,
      sessionIdsWithHostChanges,
    } = get();

    return {
      isEventStartChanged: !isNil(eventStart),
      isOccurrencesChanged:
        sessionType === RecurringType.RECURRING && !isNil(occurrences) && occurrences.length > 0,
      isAdditionalSessionsChanged:
        sessionType === RecurringType.MULTI &&
        !isNil(additionalSessions) &&
        additionalSessions.length > 0,
      isSessionIdsToCancelChanged: sessionIdsToCancel.length > 0,
      isHostedByChanged: sessionIdsWithHostChanges.length > 0,
    };
  },
  getErrors: (navItem: NavigationItems) => {
    const { getWebinarSettingsErrors, getWebinarInviteeErrors, getWebinarSessionErrors } = get();

    if (navItem === NavigationItems.WEBINAR_SETTINGS) {
      return getWebinarSettingsErrors();
    }
    if (navItem === NavigationItems.SESSION_SETTINGS) {
      return getWebinarSessionErrors();
    }
    if (navItem === NavigationItems.SELECT_INVITEE) {
      return getWebinarInviteeErrors();
    }

    return [];
  },
  getWebinarSettingsErrors: () => {
    const errors: WebinarError[] = [];
    const { name } = get();

    if (!name || !name?.length) {
      errors.push(WebinarError.NAME_REQUIRED);
    }

    return errors;
  },
  getWebinarInviteeErrors: () => {
    const errors: WebinarError[] = [];
    const {
      invitedUsers,
      invitedWorkerTeams,
      invitedQualifications,
      invitedSquads,
      audience,
      cohortMappings,
    } = get();

    if (
      !invitedUsers.length &&
      !invitedWorkerTeams.length &&
      !invitedQualifications.length &&
      !invitedSquads.length &&
      !audience &&
      !cohortMappings.length
    ) {
      errors.push(WebinarError.INVITEES_REQUIRED);
    }
    return errors;
  },
  getWebinarSessionErrors: () => {
    const errors: WebinarError[] = [];
    const {
      createNewSessions,
      eventStart,
      duration,
      sessionType,
      occurrences,
      getRecurringErrors,
      getAdditionalSessionsErrors,
    } = get();

    if (!createNewSessions) {
      return errors;
    }

    if (!eventStart) {
      errors.push(WebinarError.START_TIME_REQUIRED);
    }
    if (eventStart && eventStart < new Date()) {
      errors.push(WebinarError.START_TIME_IN_PAST);
    }
    if (isNil(duration)) {
      errors.push(WebinarError.START_AND_END_TIME_REQUIRED);
    }
    if ((duration ?? 0) <= 0) {
      errors.push(WebinarError.END_TIME_BEFORE_START_TIME);
    }

    if (sessionType === RecurringType.RECURRING) {
      if (!occurrences || !occurrences.length) {
        errors.push(WebinarError.MISSING_RECURRING_SESSION_ERROR);
      }
      const recurringSessionErrors = getRecurringErrors();
      if (Object.values(recurringSessionErrors).some(Boolean)) {
        errors.push(WebinarError.RECURRING_SESSION_ERROR);
      }
    }
    if (sessionType === RecurringType.MULTI) {
      const additionalSessionErrors = getAdditionalSessionsErrors();
      if (Object.values(additionalSessionErrors).some(Boolean)) {
        errors.push(WebinarError.ADDITIONAL_SESSION_ERROR);
      }
    }

    return errors;
  },

  getRecurringErrors: () => {
    const errors: Record<number, WebinarError> = {};
    const { additionalTimesOfDay } = get();
    if (additionalTimesOfDay && additionalTimesOfDay?.length) {
      additionalTimesOfDay.forEach((session, index) => {
        const eventStart = getNewStartDateTime(session.date, session.startTime);
        const duration = timeDifferenceInMinutes(session.endTime, session.startTime);

        if (eventStart < new Date()) {
          errors[index] = WebinarError.START_TIME_IN_PAST;
        }
        if (duration <= 0) {
          errors[index] = WebinarError.END_TIME_BEFORE_START_TIME;
        }
      });
    }
    return errors;
  },
  getAdditionalSessionsErrors: () => {
    const errors: Record<number, WebinarError> = {};
    const { additionalSessions } = get();
    if (additionalSessions && additionalSessions?.length) {
      additionalSessions.forEach((session, index) => {
        const eventStart = getNewStartDateTime(session.date, session.startTime);
        const duration = timeDifferenceInMinutes(session.endTime, session.startTime);

        if (eventStart < new Date()) {
          errors[index] = WebinarError.START_TIME_IN_PAST;
        }
        if (duration <= 0) {
          errors[index] = WebinarError.END_TIME_BEFORE_START_TIME;
        }
      });
    }
    return errors;
  },
});

export const createEditWebinarSessionStore = (initialState?: Partial<EditWebinarSessionStore>) => {
  const store = createStore<EditWebinarSessionStore>(editWebinarSessionStoreImpl);

  if (initialState) {
    store.setState(initialState);
  }

  return store;
};
